import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import { CreateFolderButton } from '@/components/CreateFolder';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '@/components/Drawer';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/DropdownMenu';
import UploadPdfButton from '@/components/UploadPDFButton/UploadPdfButton';
import { useUploadPdfButton } from '@/components/UploadPDFButton/hooks';
import VisuallyHidden from '@/components/VisuallyHidden';
import { useMediaQuery } from '@/hooks';
import Add from '@/icons/add.svg';
import Folder from '@/icons/folder.svg';
import UploadFile from '@/icons/upload-file.svg';

function CreateNewOptionsMobile() {
	const { t } = useTranslation();
	const [openCreateNewDrawer, setOpenCreateNewDrawer] = useState(false);
	return (
		<Drawer open={openCreateNewDrawer} onOpenChange={setOpenCreateNewDrawer}>
			<DrawerTrigger asChild>
				<Button
					onClick={() => {
						setOpenCreateNewDrawer(true);
					}}
				>
					<Add />
				</Button>
			</DrawerTrigger>
			<DrawerContent className="gap-1 px-5 pb-8 pt-6">
				<VisuallyHidden asChild>
					<DrawerHeader>
						<DrawerTitle>
							{t('myDocuments.modals.mobileDescription')}
						</DrawerTitle>
						<DrawerDescription>
							{t('myDocuments.modals.mobileDescription')}
						</DrawerDescription>
					</DrawerHeader>
				</VisuallyHidden>
				<div className="flex flex-col items-start gap-2 py-1 text-xs leading-5 text-grey-500 hover:cursor-pointer">
					<UploadPdfButton className="h-auto gap-2 bg-transparent px-3 text-sm font-normal text-grey-500 hover:bg-grey-100">
						<UploadFile size={17} />
						<span>{t('myDocuments.document')}</span>
					</UploadPdfButton>

					<CreateFolderButton className="h-auto gap-2 bg-transparent px-3 text-sm font-normal text-grey-500 hover:bg-grey-100">
						<Folder size={17} />
						<span>{t('myDocuments.folder')}</span>
					</CreateFolderButton>
				</div>
			</DrawerContent>
		</Drawer>
	);
}

function CreateNewOptionsDesktop() {
	const { t } = useTranslation();
	const [openCreateNewDialog, setOpenCreateNewDialog] = useState(false);
	const {
		inputProps,
		loading: uploadPdfLoading,
		onClick: onUploadPdfButtonClick
	} = useUploadPdfButton();

	function onItemClick(event) {
		const { target } = event;
		if (target.classList.contains('dropdown-item')) {
			target.firstElementChild.click();
		}
	}

	return (
		<DropdownMenu
			open={openCreateNewDialog}
			onOpenChange={setOpenCreateNewDialog}
		>
			<input {...inputProps} />
			<DropdownMenuTrigger asChild>
				<Button
					loading={uploadPdfLoading}
					onClick={() => {
						setOpenCreateNewDialog(true);
					}}
				>
					{t('myDocuments.createNew')}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" side="bottom" forceMount>
				<DropdownMenuItem className="dropdown-item" onClick={onItemClick}>
					<Button
						className="h-auto gap-2 bg-transparent p-0 text-sm font-normal text-grey-500 hover:bg-grey-100"
						onClick={onUploadPdfButtonClick}
					>
						<UploadFile size={17} />
						<span>{t('myDocuments.document')}</span>
					</Button>
				</DropdownMenuItem>
				<DropdownMenuItem className="dropdown-item" onClick={onItemClick}>
					<CreateFolderButton className="h-auto gap-2 bg-transparent p-0 text-sm font-normal text-grey-500 hover:bg-grey-100">
						<Folder size={17} />
						<span>{t('myDocuments.folder')}</span>
					</CreateFolderButton>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function CreateNewButton() {
	const mdDown = useMediaQuery('mdDown');

	return mdDown ? <CreateNewOptionsMobile /> : <CreateNewOptionsDesktop />;
}

export default CreateNewButton;
